import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Sparkles, Check, ArrowUpRight, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, stagger, viewportOnce, EASE } from "@/lib/motion";
import { SPOTS_LEFT } from "@/lib/foundation";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/landing/Logo";
import RevealDialog from "@/pages/pricing/RevealDialog";

// Where signup lives — env-overridable so staging/prod can differ.
const REGISTER_APP_URL =
  process.env.REACT_APP_REGISTER_URL || "https://app.bookaway.com.au/register";

/** Compose the app.bookaway.com.au/register URL, forwarding the lead
 * details as query params so the business never has to re-type them. */
function buildRegisterUrl(lead, tier) {
  const params = new URLSearchParams();
  if (lead) {
    if (lead.business_name) params.set("business_name", lead.business_name);
    if (lead.contact_name) params.set("contact_name", lead.contact_name);
    if (lead.email) params.set("email", lead.email);
    if (lead.phone) params.set("phone", lead.phone);
    if (lead.city) params.set("city", lead.city);
    if (Array.isArray(lead.categories) && lead.categories.length) {
      params.set("categories", lead.categories.join(","));
    }
  }
  if (tier) params.set("tier", tier);
  const qs = params.toString();
  return qs ? `${REGISTER_APP_URL}?${qs}` : REGISTER_APP_URL;
}

const TIERS = [
  {
    id: "pro",
    name: "Pro",
    price: "$59",
    cadence: "per month",
    fee: "Standard membership",
    features: ["Standard booking dashboard", "Unlimited posts", "Basic analytics"],
    highlight: false,
  },
  {
    id: "foundation",
    name: "Foundation",
    specialPrice: "$49",
    cadence: "/mo",
    originalPrice: "$99/mo",
    note: "First 20 businesses \u00b7 all Platinum features for life",
    features: [
      "Priority Map & Search placement",
      "White Glove onboarding",
      "\u2018Featured Business\u2019 badge",
      "Monthly Performance Reports",
    ],
    highlight: true,
  },
  {
    id: "platinum",
    name: "Platinum",
    price: "$99",
    cadence: "per month",
    fee: "Premium membership",
    features: [
      "Priority Map & Search placement",
      "White Glove onboarding",
      "\u2018Featured Business\u2019 badge",
      "Monthly Performance Reports",
    ],
    highlight: false,
  },
];

/** Small helper — obscured price placeholder with a soft blur badge. */
function LockedPrice({ variant = "light" }) {
  const tone =
    variant === "dark"
      ? "text-white/80 border-white/20 bg-white/5"
      : "text-[#2A2724] border-black/15 bg-white/60";
  return (
    <span
      data-testid="locked-price"
      className={`inline-flex select-none items-center gap-2 rounded-xl border px-3 py-1.5 font-serif text-3xl leading-none ${tone}`}
      aria-label="Price hidden — reveal to see"
    >
      <Lock className="h-4 w-4 opacity-70" />
      <span className="[filter:blur(6px)] tracking-widest">$••</span>
    </span>
  );
}

function Tier({ tier, revealed, lead, onSeePricing }) {
  const handleCta = () => {
    if (!revealed) return onSeePricing(tier);
    const url = buildRegisterUrl(lead, tier.id);
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const ctaLabel = revealed ? "Register your business" : "See pricing";

  if (tier.highlight) {
    return (
      <motion.div
        variants={fadeUp}
        data-testid={`pricing-tier-${tier.id}`}
        className="relative z-10 overflow-hidden rounded-3xl border border-gold bg-[#2A2724] p-8 pt-12 text-white shadow-[0_40px_90px_-40px_rgba(212,175,55,0.6)] lg:-translate-y-4 lg:scale-[1.05]"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.3),transparent_70%)]" />
        <div className="absolute right-0 top-0 rounded-bl-2xl bg-gold px-4 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-black">
          Best Value
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-1.5 text-xs font-medium tracking-wide text-gold">
          <Sparkles className="h-3.5 w-3.5" />
          50% off Platinum · Only {SPOTS_LEFT} spots left
        </span>
        <h3 className="mt-6 flex items-center gap-2.5 font-serif text-3xl text-white">
          <Crown className="h-6 w-6 text-gold" />
          {tier.name}
        </h3>
        <div className="mt-4 flex items-end gap-2">
          {revealed ? (
            <>
              <span className="font-serif text-6xl leading-none text-gold">
                {tier.specialPrice}
              </span>
              <span className="mb-1 text-sm text-white/60">{tier.cadence}</span>
            </>
          ) : (
            <LockedPrice variant="dark" />
          )}
        </div>
        {revealed && (
          <p
            data-testid="foundation-trial-pill"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/15 px-3 py-1.5 text-xs font-medium text-gold"
          >
            <Sparkles className="h-3.5 w-3.5" />
            2 months free when you join as a Foundation member
          </p>
        )}
        {revealed ? (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-white/40 line-through">{tier.originalPrice}</span>
            <span className="rounded-full bg-gold/15 px-2.5 py-0.5 text-xs font-medium text-gold">
              50% off Platinum
            </span>
          </div>
        ) : (
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-white/50">
            Unlock the Foundation rate
          </p>
        )}
        <p className="mt-3 text-xs uppercase tracking-[0.16em] text-white/50">{tier.note}</p>
        <ul className="mt-8 space-y-4">
          {tier.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-white/85">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              {f}
            </li>
          ))}
        </ul>
        <button
          data-testid={`pricing-cta-${tier.id}`}
          onClick={handleCta}
          className="group mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-[#AA8627]"
        >
          {revealed ? "Claim your Foundation spot" : "See pricing"}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </motion.div>
    );
  }
  return (
    <motion.div
      variants={fadeUp}
      data-testid={`pricing-tier-${tier.id}`}
      className="rounded-3xl border border-black/10 bg-[#F7F5F2] p-8"
    >
      <h3 className="font-serif text-2xl text-[#2A2724]">{tier.name}</h3>
      <div className="mt-3 flex items-end gap-2">
        {revealed ? (
          <>
            <span className="font-serif text-5xl leading-none text-[#2A2724]">{tier.price}</span>
            <span className="mb-1 text-sm text-[#6B6B6B]">{tier.cadence}</span>
          </>
        ) : (
          <LockedPrice />
        )}
      </div>
      <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#9a9a9a]">{tier.fee}</p>
      <ul className="mt-8 space-y-4">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-[#6B6B6B]">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            {f}
          </li>
        ))}
      </ul>
      <button
        data-testid={`pricing-cta-${tier.id}`}
        onClick={handleCta}
        className="mt-9 inline-flex w-full items-center justify-center rounded-full border border-black/20 px-8 py-4 text-sm text-[#2A2724] transition-colors duration-300 hover:border-gold hover:text-gold"
      >
        {ctaLabel}
      </button>
    </motion.div>
  );
}

export default function Pricing() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Prices stay hidden until the visitor submits the reveal form.
  // Persisted in localStorage so a page refresh keeps them unlocked.
  const [revealed, setRevealed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTier, setActiveTier] = useState(null);
  const [lead, setLead] = useState(null);

  useEffect(() => {
    try {
      if (localStorage.getItem("bookaway_pricing_revealed") === "1") {
        setRevealed(true);
      }
      const raw = localStorage.getItem("bookaway_business_lead");
      if (raw) setLead(JSON.parse(raw));
    } catch {}
  }, []);

  const onSeePricing = (tier) => {
    setActiveTier(tier);
    setDialogOpen(true);
  };

  const onSignOut = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/");
  };

  return (
    <div data-testid="pricing-page" className="relative min-h-screen bg-[#F7F5F2]">
      <div className="grain" />

      {/* Top bar */}
      <header className="relative z-10 border-b border-black/10 bg-[#F7F5F2]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-6 md:px-12">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={44} />
            <span className="font-serif text-2xl font-semibold tracking-tight text-[#2A2724]">
              Book Away
            </span>
          </Link>
          <div className="flex items-center gap-4">
            {user?.email ? (
              <>
                <div className="hidden text-right sm:block">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#9a9a9a]">Signed in</p>
                  <p data-testid="pricing-user-email" className="text-sm text-[#2A2724]">
                    {user?.email}
                  </p>
                </div>
                <button
                  data-testid="pricing-signout"
                  onClick={onSignOut}
                  className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm text-[#2A2724] transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </>
            ) : (
              lead?.business_name && (
                <p
                  data-testid="pricing-lead-badge"
                  className="hidden text-right sm:block"
                >
                  <span className="text-xs uppercase tracking-[0.22em] text-[#9a9a9a]">
                    Showing pricing for
                  </span>
                  <br />
                  <span className="text-sm text-[#2A2724]">{lead.business_name}</span>
                </p>
              )
            )}
          </div>
        </div>
      </header>

      <section className="relative z-10 px-6 py-20 md:px-12 md:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <p className="overline mb-4">Membership options</p>
            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2724]"
            >
              Simple plans. One <span className="italic text-gold">founding</span> advantage.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
              className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-[#6B6B6B]"
            >
              Choose the tier that fits your studio. Foundation Members lock in a
              lifetime discount and permanent priority placement — only{" "}
              <span className="font-medium text-gold">{SPOTS_LEFT} spots</span> remain.
            </motion.p>
            {!revealed && (
              <p
                data-testid="pricing-locked-hint"
                className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-gold"
              >
                <Lock className="h-3.5 w-3.5" />
                Pricing hidden · Tap any tier to reveal
              </p>
            )}
          </div>

          <motion.div
            variants={stagger(0.12)}
            initial="hidden"
            animate="show"
            className="mx-auto grid grid-cols-1 items-center gap-6 lg:grid-cols-3"
          >
            {TIERS.map((t) => (
              <Tier
                key={t.id}
                tier={t}
                revealed={revealed}
                lead={lead}
                onSeePricing={onSeePricing}
              />
            ))}
          </motion.div>
        </div>
      </section>

      <RevealDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        tier={activeTier}
        onRevealed={() => setRevealed(true)}
      />
    </div>
  );
}
